/**
 * Dataspace Tree Store - Global filesystem tree for entire dataspace
 * 
 * Data Model: Edge documents + fractional indexing (same as spaceTreeStore)
 * - Each edge: (dataspace_id, parent_id, child_id, pos, expanded)
 * - Edges stored in separate collection (not on entity.skeleton)
 * - Supports aliases (same entity can have multiple edges)
 * 
 * Key Differences from SpaceTreeStore:
 * - ✅ No spaceId filtering - shows ALL entities in dataspace
 * - ✅ Persists to dataspace_edges API (not entity.skeleton)
 * - ✅ Supports aliases (entity in multiple locations)
 * - ✅ User-agnostic (shared tree for entire dataspace/org)
 * 
 * Architecture (same as spaceTreeStore):
 * - ✅ Optimistic updates (instant UI feedback)
 * - ✅ Operation log for persistence (debounced flush)
 * - ✅ Subscribes to entityStore for reactive node creation/deletion
 */

import { create } from 'zustand';
import { BaseEntityType } from '../types/entities';
import useEntityStore from './entityStore';
import { createFileLogger } from '../utils/logger';
import fetchService from '../services/fetchService';
import { API_BASE_URL } from '../api/config';

const log = createFileLogger('DATASPACE_TREE_STORE', { enabled: false });
const VERBOSE_LOGGING = false;

const TREE_STORE_VERSION = 'v2.0-alias-edges'; // ✅ Changed: Now supports aliases with edgeKey format
log.log('🌲 [DataspaceTreeStore] Loaded version:', TREE_STORE_VERSION);

// ✅ MIGRATION: Force clear tree on version change
const STORED_VERSION_KEY = 'dataspaceTreeStore_version';
const storedVersion = localStorage.getItem(STORED_VERSION_KEY);
if (storedVersion !== TREE_STORE_VERSION) {
  log.log('🔄 [DataspaceTreeStore] Version changed from', storedVersion, 'to', TREE_STORE_VERSION);
  log.log('🔄 [DataspaceTreeStore] Clearing old tree data and forcing refresh');
  localStorage.setItem(STORED_VERSION_KEY, TREE_STORE_VERSION);
  // Set flag to force refresh on next initialize()
  (window as any).__forceDataspaceTreeRefresh = true;
  
  // ✅ CRITICAL: Also immediately reset the store state to clear old format data
  // This ensures any already-created store instance gets cleared
  setTimeout(() => {
    const store = useDataspaceTreeStore.getState();
    if (Object.keys(store.nodesById).length > 0) {
      log.log('🔄 [DataspaceTreeStore] Clearing', Object.keys(store.nodesById).length, 'old format nodes');
      // Capture dataspaceId before clearing — if initialize() already ran (race condition),
      // we must re-run it after clearing since the component's useEffect won't re-fire.
      const prevDataspaceId = store.dataspaceId;
      useDataspaceTreeStore.setState({
        nodesById: {},
        childrenByParentId: new Map(),
        initialized: false,
        rebuildCount: 0,
        dataspaceId: null
      });
      if (prevDataspaceId) {
        log.log('🔄 [DataspaceTreeStore] Re-initializing after version migration clear:', prevDataspaceId);
        useDataspaceTreeStore.getState().initialize(prevDataspaceId);
      }
    }
  }, 0);
}

// ========================================================================
// Fractional Indexing (copied from spaceTreeStore)
// ========================================================================

function generatePositionBetween(before: string | null, after: string | null): string {
  // ✅ CRITICAL FIX: Detect and handle position collision
  // This can happen due to race conditions or database inconsistencies
  if (before && after && before === after) {
    log.warn('[FractionalIndex] ⚠️ Position collision detected!', { 
      before, 
      after, 
      note: 'Appending digit to create unique position' 
    });
    // Append '0' to create unique position after the collision point
    return before + '0';
  }
  
  if (!before && !after) return 'M';
  if (!after) return before + 'M';
  if (!before) {
    const firstChar = after[0];
    const firstVal = charToBase36(firstChar);
    if (firstVal > 0) {
      const mid = Math.floor(firstVal / 2);
      return base36ToChar(mid);
    }
    return '!' + after;
  }
  
  let pos = 0;
  while (pos < before.length && pos < after.length && before[pos] === after[pos]) {
    pos++;
  }
  
  const prefix = before.slice(0, pos);
  
  if (pos === before.length) {
    const afterSuffix = after.slice(before.length);
    const firstAfterChar = afterSuffix[0];
    const firstAfterVal = charToBase36(firstAfterChar);
    
    if (firstAfterVal >= 2) {
      const midVal = Math.floor(firstAfterVal / 2);
      return before + base36ToChar(midVal);
    }
    return before + '0' + 'M';
  }
  
  if (pos === after.length) {
    return after + 'V';
  }
  
  const beforeChar = before[pos];
  const afterChar = after[pos];
  const beforeVal = charToBase36(beforeChar);
  const afterVal = charToBase36(afterChar);
  
  if (afterVal - beforeVal >= 2) {
    const midVal = Math.floor((beforeVal + afterVal) / 2);
    return prefix + base36ToChar(midVal);
  }
  
  return before + 'M';
}

function charToBase36(char: string): number {
  const code = char.charCodeAt(0);
  if (code === 33) return -1;
  if (code >= 48 && code <= 57) return code - 48;
  if (code >= 65 && code <= 90) return code - 65 + 10;
  if (code >= 97 && code <= 122) return code - 97 + 36;
  return 0;
}

function base36ToChar(val: number): string {
  if (val < 10) return String.fromCharCode(val + 48);
  if (val < 36) return String.fromCharCode(val - 10 + 65);
  if (val < 62) return String.fromCharCode(val - 36 + 97);
  return '0';
}

function generateCleanPosition(index: number): string {
  if (index < 10) {
    return String.fromCharCode(48 + index);
  } else if (index < 36) {
    return String.fromCharCode(65 + (index - 10));
  } else if (index < 62) {
    return String.fromCharCode(97 + (index - 36));
  } else {
    const multiIndex = index - 62;
    const firstIndex = Math.floor(multiIndex / 26);
    const secondIndex = multiIndex % 26;
    const firstChar = String.fromCharCode(48 + (firstIndex % 10));
    const secondChar = String.fromCharCode(65 + secondIndex);
    return firstChar + secondChar;
  }
}

// ========================================================================
// Tree Node & Edge Structures
// ========================================================================

interface TreeNode {
  id: string; // Entity ID
  parentId: string | null;
  pos: string;
  expanded: boolean;
  kind?: 'canonical' | 'alias';
}

// ✅ Helper to create unique edge key for alias support
// EXPORTED for use in other components
export function getEdgeKey(entityId: string, parentId: string | null): string {
  return `${parentId || 'ROOT'}:${entityId}`;
}

interface DataspaceEdge {
  id: string;
  dataspaceId: string;
  parentId: string | null;
  childId: string;
  pos: string;
  expanded: boolean;
  kind: 'canonical' | 'alias';
  createdAt: string;
  updatedAt: string;
}

// TreeOperation is kept for potential future use but no longer used for persistence.
type TreeOperation = 
  | { type: 'move'; nodeId: string; parentId: string | null; pos: string; timestamp: number }
  | { type: 'expand'; nodeId: string; expanded: boolean; timestamp: number }
  | { type: 'create'; nodeId: string; parentId: string | null; pos: string; kind: 'canonical' | 'alias'; timestamp: number }
  | { type: 'delete'; nodeId?: string; parentId?: string | null; timestamp: number };

// ========================================================================
// Per-user expanded state (localStorage, not shared via CRDT)
// ========================================================================

const _EXPANDED_LS_KEY = 'dataspaceTree_expanded';

function _loadExpandedSet(): Set<string> {
  try {
    const raw = localStorage.getItem(_EXPANDED_LS_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch { return new Set(); }
}

function _saveExpandedSet(expanded: Set<string>): void {
  try {
    localStorage.setItem(_EXPANDED_LS_KEY, JSON.stringify([...expanded]));
  } catch { /* quota exceeded — non-fatal */ }
}

const _expandedNodes = _loadExpandedSet();

function _isExpandedLocal(nodeId: string): boolean {
  return _expandedNodes.has(nodeId);
}

function _setExpandedLocal(nodeId: string, expanded: boolean): void {
  if (expanded) _expandedNodes.add(nodeId);
  else _expandedNodes.delete(nodeId);
  _saveExpandedSet(_expandedNodes);
}

// ========================================================================
// Store State
// ========================================================================

interface DataspaceTreeState {
  dataspaceId: string | null;
  nodesById: Record<string, TreeNode>; // Keyed by edgeKey "(parentId or 'ROOT'):childId"
  childrenByParentId: Map<string | null, string[]>;
  initialized: boolean;
  rebuildCount: number;
  entityStoreUnsubscribe: (() => void) | null;
  entityEventUnsubscribe: (() => void) | null;
}

interface DataspaceTreeStore extends DataspaceTreeState {
  initialize: (dataspaceId: string) => Promise<void>;
  initFromEdges: (edges: DataspaceEdge[]) => void;
  migrateSpaceEntities: (spaceId: string) => Promise<void>;

  moveNode: (nodeId: string, newParentId: string | null, afterNodeId: string | null, fromParentId?: string | null) => void;
  toggleExpanded: (nodeId: string) => void;
  expandNode: (nodeId: string) => void;
  collapseNode: (nodeId: string) => void;
  expandAllNodes: () => { count: number };
  collapseAllNodes: () => { count: number };
  ensureNodeExists: (nodeId: string, parentId: string | null, kind?: 'canonical' | 'alias') => void;
  addAliasEdge: (entityId: string, parentId: string | null) => Promise<void>;

  getRootNodeIds: () => string[];
  getChildNodeIds: (parentId: string | null) => string[];
  getNode: (nodeId: string, parentId?: string | null) => TreeNode | undefined;
  isExpanded: (nodeId: string, parentId?: string | null) => boolean;
  hasChildren: (nodeId: string) => boolean;

  _rebuildChildrenMap: () => void;
  _applyAddNode: (edgeKey: string, node: TreeNode, operation?: TreeOperation) => void;
  _applyRemoveNode: (edgeKey: string, options?: { persist?: boolean }) => void;
  _applyEdgeBatch: (adds: [string, TreeNode][], removes: string[]) => void;

  cleanup: () => void;
  debugPrintTree: () => void;
}

// ========================================================================
// Helper: Filter displayable entities
// ========================================================================

function isDisplayableEntity(entity: BaseEntityType): boolean {
  // ✅ Spaces ARE displayable - they're organizeable in the tree!
  // Mail.Message is hidden — it's thread content (like DocVersion), not a browseable item.
  const hiddenTypes = ['Message', 'DocVersion', 'Mail.Message', 'Messaging.Message'];
  if (hiddenTypes.includes(entity.entityType)) return false;
  if ((entity as any).hidden === true) return false;
  if ((entity as any).trashed === true) return false;
  
  // ✅ Integration data entities are system-managed and should NOT appear in the
  // space sidebar tree. They're queryable via river.entity.query() in apps but
  // are not user-organizable documents. They either have no spaceId (bank data,
  // integration state) or belong to a specific integration hierarchy.
  // Note: 'Integration' (generic) stays blocked. Named sub-types like
  // 'Integration.Gmail' are explicitly allowed below as mountable volumes.
  const integrationDataTypes = [
    'BankTransaction',
    'PlaidItem',
    'PlaidTransaction',
    'PlaidAccount',
    'Integration',
    'GoogleDriveFile',
    'LocalFile',
  ];
  if (integrationDataTypes.includes(entity.entityType)) return false;
  
  // ✅ Entities with no spaceId that are NOT explicitly user-organizable content
  // should not appear in the sidebar tree. User content (Docs, Notes, Sheets etc.)
  // always has a spaceId from creation. Integration-sourced entities typically don't.
  const userContentTypes = [
    'Doc', 'Note', 'Sheet', 'Canvas', 'Presentation',
    'Group', 'Space', 'Thread', 'Tool', 'Corpus', 'Dataset',
    'Rule',
    'app_project', 'website_project', 'code_project',
    'APIConnection', 'MCPServer',
    'File', 'File.Image', 'File.Video', 'File.Document', 'File.Document.PDF',
    'StoryCharacter',
    // ✅ Mail integration hierarchy — Integration.Gmail is a mountable volume.
    // Mail.Folder, Mail.Thread, Mail.Draft are managed exclusively by mailAppStore
    // and are not part of the dataspace tree.
    'Integration.Gmail', 'Integration.Outlook', 'Integration.IMAP',
    'Integration.WhatsApp',
    'Messaging.Conversation',
    // ✅ Banking integration hierarchy — Integration.Plaid is a mountable volume,
    // BankAccount entities are its browseable children.
    // BankTransaction is intentionally absent (too numerous, shown only in BankApp).
    'Integration.Plaid',
    'BankAccount',
  ];
  
  // If entity type is not a recognized user content type AND has no spaceId, hide it
  const entityType = entity.entityType || '';
  const isKnownUserContent = userContentTypes.some(t => 
    entityType === t || entityType.startsWith(t + '.')
  );
  const hasSpaceId = !!(entity as any).spaceId;
  
  if (!isKnownUserContent && !hasSpaceId) return false;
  
  return true;
}

// ========================================================================
// Store Implementation
// ========================================================================

export const useDataspaceTreeStore = create<DataspaceTreeStore>((set, get) => ({
  // Initial state
  dataspaceId: null,
  nodesById: {},
  childrenByParentId: new Map(),
  initialized: false,
  rebuildCount: 0,
  entityStoreUnsubscribe: null,
  entityEventUnsubscribe: null,
  
  // ========================================================================
  // Initialization
  // ========================================================================
  
  initialize: async (dataspaceId: string) => {
    const { initialized: currentlyInit, dataspaceId: currentDataspaceId, entityStoreUnsubscribe, entityEventUnsubscribe } = get();

    log.log('[DataspaceTreeStore] 🚀 Initializing for dataspace:', dataspaceId);

    // Cleanup previous subscriptions if switching dataspaces
    if (currentlyInit && currentDataspaceId !== dataspaceId) {
      if (entityStoreUnsubscribe) {
        entityStoreUnsubscribe();
        set({ entityStoreUnsubscribe: null });
      }
      if (entityEventUnsubscribe) {
        entityEventUnsubscribe();
        set({ entityEventUnsubscribe: null });
      }
    }

    // Check for version migration flag or old key format
    const forceRefresh = (window as any).__forceDataspaceTreeRefresh;

    if (currentlyInit && currentDataspaceId === dataspaceId) {
      log.log('[DataspaceTreeStore] Already initialized for this dataspace');

      if (forceRefresh) {
        log.log('[DataspaceTreeStore] 🔄 Version migration - forcing refresh');
        (window as any).__forceDataspaceTreeRefresh = false;
        set({ nodesById: {}, childrenByParentId: new Map(), initialized: false, rebuildCount: 0 });
      } else {
        const { nodesById } = get();
        const firstKey = Object.keys(nodesById)[0];
        const usesOldFormat = firstKey && !firstKey.includes(':');
        if (usesOldFormat) {
          log.log('[DataspaceTreeStore] ⚠️ Old key format detected, forcing refresh');
          set({ nodesById: {}, childrenByParentId: new Map(), initialized: false, rebuildCount: 0 });
        } else {
          return;
        }
      }
    }

    set({ dataspaceId, initialized: false });
    console.log('[TREE_INIT] 🌲 initialize() called with dataspaceId:', dataspaceId);

    try {
      // Build tree from org Y.Doc (seeded from MongoDB on cold start, kept current by backend broadcasts).
      // This is the canonical read path — no REST roundtrip needed.
      // If the Y.Doc is unavailable (WS server down), fall back to REST.
      const { waitForOrgSync, getOrgEdgesSnapshot } = await import('./orgYDocStore');

      let edges: DataspaceEdge[] = [];
      let source = 'Y.Doc';
      const initStart = Date.now();

      try {
        await waitForOrgSync(8000);
        const snapshot = getOrgEdgesSnapshot();
        edges = snapshot.map(e => ({
          id: e.edgeId,
          dataspaceId,
          parentId: e.parentId,
          childId: e.childId,
          pos: e.pos,
          expanded: e.expanded,
          kind: e.kind,
          createdAt: '',
          updatedAt: '',
        }));
        console.log(`[TreeInit] ✅ Source: Y.Doc | edges: ${edges.length} | time: ${Date.now() - initStart}ms`);
      } catch (syncErr) {
        // Y.Doc unavailable (WS server down) — fall back to REST for this cold start
        console.warn('[DataspaceTreeStore] ⚠️ Y.Doc sync timeout — falling back to REST');
        source = 'REST-FALLBACK';
        const url = `${API_BASE_URL}/dataspaces/${dataspaceId}/edges`;
        const response = await fetchService(url, { credentials: 'include' });
        if (!Array.isArray(response)) throw new Error(`Expected edge array, got ${typeof response}`);
        edges = response;
        console.log(`[TreeInit] ⚠️ Source: REST-FALLBACK | edges: ${edges.length} | time: ${Date.now() - initStart}ms`);
      }

      get().initFromEdges(edges);

      const treeNodeCount = Object.keys(get().nodesById).length;
      console.log(`[TreeInit] 🌳 Tree built: ${treeNodeCount} nodes from ${source}`);
      console.log(`[DataspaceTreeStore] Y.Map key format: semantic (parentId:childId) | sample: ${edges[0]?.id ?? 'n/a'}`);

      // Subscribe to entity deletions — removed entities must be pruned from the tree.
      // New entities arrive via the org Y.Doc edge observer (_handleEdgeChanges), not here.
      const unsubscribe = useEntityStore.subscribe((state: any, prevState: any) => {
        const { dataspaceId: currentDs } = get();
        if (!currentDs) return;

        const deletedEntityIds = prevState.entities
          .filter((e: BaseEntityType) => !state.entities.find((curr: BaseEntityType) => curr._id === e._id))
          .map((e: BaseEntityType) => e._id);

        if (deletedEntityIds.length > 0) {
          // Guard: bulk reload clears then repopulates — skip to avoid mass edge wipe
          if (state.entities.length === 0 && prevState.entities.length > 0) {
            log.warn('[DataspaceTreeStore] entityStore cleared — skipping deletion detection');
            return;
          }
          log.log('[DataspaceTreeStore] 🗑️ Detected', deletedEntityIds.length, 'deleted entities');
          const currentNodesById = get().nodesById;
          deletedEntityIds.forEach((entityId: string) => {
            Object.keys(currentNodesById).forEach(edgeKey => {
              if (currentNodesById[edgeKey].id === entityId) {
                get()._applyRemoveNode(edgeKey, { persist: true });
              }
            });
          });
        }
      });

      console.log(`[TREE_INIT] ✅ initialized=true — tree is ready. Nodes: ${Object.keys(get().nodesById).length}`);
      set({ initialized: true, entityStoreUnsubscribe: unsubscribe });

      // Subscribe to entity creation events for observability (edges arrive via Y.Doc observer)
      (async () => {
        try {
          const { entityEventService } = await import('../services/entityEventService');
          const eventUnsubscribe = entityEventService.onEntityEvent('created', (event) => {
            const { initialized: isInit } = get();
            if (!isInit || !isDisplayableEntity(event.entity)) return;
            log.log('[DataspaceTreeStore] 📥 Entity created:', event.entity.entityType, event.entity.name);
          });
          set({ entityEventUnsubscribe: eventUnsubscribe });
          log.log('[DataspaceTreeStore] ✅ Subscribed to entity creation events');
        } catch (error) {
          log.error('[DataspaceTreeStore] Failed to subscribe to entity events:', error);
        }
      })();

      log.log('[DataspaceTreeStore] ✅ Initialization complete');

    } catch (error) {
      console.error('[TREE_INIT] ❌ Initialization FAILED:', error);
      set({ initialized: false });
      setTimeout(() => {
        const { dataspaceId: currentId, initialized: isInit } = get();
        if (!isInit && currentId === dataspaceId) {
          log.log('[DataspaceTreeStore] 🔄 Retrying initialization for:', dataspaceId);
          get().initialize(dataspaceId);
        }
      }, 3000);
    }
  },
  
  initFromEdges: (edges: DataspaceEdge[]) => {
    log.log('[DataspaceTreeStore] 🔨 Building tree from', edges.length, 'edges');

    // Filter out trashed entities (their edges are stale)
    const trashedEntityIds = new Set(
      useEntityStore.getState().entities.filter((e: any) => e.trashed === true).map((e: any) => e._id)
    );
    const validEdges = edges.filter((edge: DataspaceEdge) => !trashedEntityIds.has(edge.childId));
    if (validEdges.length < edges.length) {
      log.warn('[DataspaceTreeStore] ⚠️ Filtered out', edges.length - validEdges.length, 'trashed-entity edges');
    }

    // Deduplicate canonical edges per child (safety net for legacy data; should be no-ops with Y.Map keys)
    const edgesByChild = new Map<string, DataspaceEdge[]>();
    validEdges.forEach((edge: DataspaceEdge) => {
      const existing = edgesByChild.get(edge.childId) || [];
      existing.push(edge);
      edgesByChild.set(edge.childId, existing);
    });

    const deduplicatedEdges: DataspaceEdge[] = [];
    edgesByChild.forEach((entityEdges, childId) => {
      if (entityEdges.length === 1) {
        deduplicatedEdges.push(entityEdges[0]);
        return;
      }
      const canonicalEdges = entityEdges.filter(e => e.kind !== 'alias');
      const aliasEdges = entityEdges.filter(e => e.kind === 'alias');
      if (canonicalEdges.length > 1) {
        const sorted = canonicalEdges.sort((a, b) => {
          if (a.parentId !== null && b.parentId === null) return -1;
          if (a.parentId === null && b.parentId !== null) return 1;
          const aIsOld = typeof a.pos === 'string' && /^M+$/.test(a.pos);
          const bIsOld = typeof b.pos === 'string' && /^M+$/.test(b.pos);
          if (!aIsOld && bIsOld) return -1;
          if (aIsOld && !bIsOld) return 1;
          return (b.id || '').localeCompare(a.id || '');
        });
        deduplicatedEdges.push(sorted[0]);
        log.warn('[DataspaceTreeStore] 🔁 Deduped', canonicalEdges.length, 'edges for', childId);
      } else {
        deduplicatedEdges.push(...canonicalEdges);
      }
      if (aliasEdges.length > 0) deduplicatedEdges.push(...aliasEdges);
    });

    const nodesById: Record<string, TreeNode> = {};
    deduplicatedEdges.forEach((edge: DataspaceEdge) => {
      const edgeKey = getEdgeKey(edge.childId, edge.parentId);
      nodesById[edgeKey] = {
        id: edge.childId,
        parentId: edge.parentId,
        pos: edge.pos,
        expanded: _isExpandedLocal(edge.childId),
        kind: edge.kind,
      };
    });

    log.log('[DataspaceTreeStore] Built', Object.keys(nodesById).length, 'nodes from edges');
    set({ nodesById, rebuildCount: get().rebuildCount + 1 });
    get()._rebuildChildrenMap();
    log.log('[DataspaceTreeStore] ✅ Tree built with', Object.keys(nodesById).length, 'nodes');
  },
  
  // ========================================================================
  // Tree Operations (same API as spaceTreeStore)
  // ========================================================================
  
  moveNode: (nodeId: string, newParentId: string | null, afterNodeId: string | null, fromParentId?: string | null) => {
    log.log('[DataspaceTreeStore] 🎯 moveNode:', { nodeId: nodeId.slice(-4), to: newParentId?.slice(-4), afterNodeId: afterNodeId?.slice(-4) });

    let success = false;
    let moveData: { childId: string; oldParentId: string | null; newParentId: string | null; pos: string; expanded: boolean; kind: string } | null = null;

    set(state => {
      const { nodesById, childrenByParentId, rebuildCount } = state;

      const oldEdgeKey = fromParentId !== undefined
        ? getEdgeKey(nodeId, fromParentId)
        : Object.keys(nodesById).find(key => nodesById[key]?.id === nodeId);

      if (!oldEdgeKey) {
        log.warn('[DataspaceTreeStore] moveNode: node not found:', nodeId);
        console.error('⚠️ Failed to move item - node not found');
        return state;
      }

      const node = nodesById[oldEdgeKey];
      if (!node) {
        console.error('[DataspaceTreeStore] moveNode: edge not found. oldEdgeKey:', oldEdgeKey,
          '| node IDs with this id:', Object.entries(nodesById).filter(([, n]) => n.id === nodeId).map(([k]) => k));
        return state;
      }

      const siblings = (childrenByParentId.get(newParentId) || []).filter(id => id !== nodeId);
      const afterIndex = afterNodeId ? siblings.indexOf(afterNodeId) : -1;
      const getSiblingPos = (siblingId: string, parentId: string | null): string | null =>
        nodesById[getEdgeKey(siblingId, parentId)]?.pos || nodesById[siblingId]?.pos || null;

      let newPos: string;
      if (afterIndex === -1) {
        newPos = generatePositionBetween(null, siblings.length > 0 ? getSiblingPos(siblings[0], newParentId) : null);
      } else if (afterIndex === siblings.length - 1) {
        newPos = generatePositionBetween(getSiblingPos(siblings[afterIndex], newParentId), null);
      } else {
        newPos = generatePositionBetween(getSiblingPos(siblings[afterIndex], newParentId), getSiblingPos(siblings[afterIndex + 1], newParentId));
      }
      if (!newPos) newPos = generatePositionBetween(null, null);

      const correctOldEdgeKey = getEdgeKey(nodeId, node.parentId);
      const correctNewEdgeKey = getEdgeKey(nodeId, newParentId);
      const isSameParent = node.parentId === newParentId;

      const newNodesById = { ...nodesById };
      if (oldEdgeKey !== correctOldEdgeKey) delete newNodesById[oldEdgeKey];
      if (!isSameParent) delete newNodesById[correctOldEdgeKey];
      const updatedNode = { ...node, parentId: newParentId, pos: newPos };
      newNodesById[isSameParent ? correctOldEdgeKey : correctNewEdgeKey] = updatedNode;

      moveData = { childId: node.id, oldParentId: node.parentId, newParentId, pos: newPos, expanded: node.expanded, kind: node.kind || 'canonical' };
      success = true;
      return { nodesById: newNodesById, rebuildCount: rebuildCount + 1 };
    });

    if (success && moveData) {
      get()._rebuildChildrenMap();
      log.log('[DataspaceTreeStore] ✅ moveNode complete');

      // Write to Y.Doc — the authoritative write path (replaces _flushToBackend)
      const { childId, oldParentId, newParentId: newPId, pos, expanded, kind } = moveData;
      import('./orgYDocStore').then(({ writeEdgeToOrgYDoc, deleteEdgeFromOrgYDoc }) => {
        if (oldParentId !== newPId) {
          deleteEdgeFromOrgYDoc(childId, oldParentId);
        }
        writeEdgeToOrgYDoc({ parentId: newPId, childId, pos, expanded, kind: kind as 'canonical' | 'alias' });
      }).catch(err => { console.error('[DataspaceTreeStore] moveNode: Y.Doc write failed:', err); });
    } else if (!success) {
      console.error('[DataspaceTreeStore] moveNode failed — node not found');
    }
  },
  
  toggleExpanded: (nodeId: string) => {
    let hasChanges = false;

    set(state => {
      const { nodesById, rebuildCount } = state;
      const matchingEdgeKeys = Object.keys(nodesById).filter(key => nodesById[key].id === nodeId);
      if (matchingEdgeKeys.length === 0) {
        console.warn('[DataspaceTreeStore] toggleExpanded: no matching edges for nodeId:', nodeId);
        return state;
      }
      const newNodesById = { ...nodesById };
      matchingEdgeKeys.forEach(edgeKey => {
        const node = nodesById[edgeKey];
        const newExpanded = !node.expanded;
        newNodesById[edgeKey] = { ...node, expanded: newExpanded };
        _setExpandedLocal(node.id, newExpanded);
        hasChanges = true;
      });
      if (!hasChanges) return state;
      return { nodesById: newNodesById, rebuildCount: rebuildCount + 1 };
    });

    if (hasChanges) get()._rebuildChildrenMap();
  },

  expandNode: (nodeId: string) => {
    let hasChanges = false;

    set(state => {
      const { nodesById, rebuildCount } = state;
      const matchingEdgeKeys = Object.keys(nodesById).filter(key => nodesById[key].id === nodeId);
      const newNodesById = { ...nodesById };
      matchingEdgeKeys.forEach(edgeKey => {
        const node = nodesById[edgeKey];
        if (node.expanded) return;
        newNodesById[edgeKey] = { ...node, expanded: true };
        _setExpandedLocal(node.id, true);
        hasChanges = true;
      });
      if (!hasChanges) return state;
      return { nodesById: newNodesById, rebuildCount: rebuildCount + 1 };
    });

    if (hasChanges) get()._rebuildChildrenMap();
  },

  collapseNode: (nodeId: string) => {
    let hasChanges = false;

    set(state => {
      const { nodesById, rebuildCount } = state;
      const matchingEdgeKeys = Object.keys(nodesById).filter(key => nodesById[key].id === nodeId);
      const newNodesById = { ...nodesById };
      matchingEdgeKeys.forEach(edgeKey => {
        const node = nodesById[edgeKey];
        if (!node.expanded) return;
        newNodesById[edgeKey] = { ...node, expanded: false };
        _setExpandedLocal(node.id, false);
        hasChanges = true;
      });
      if (!hasChanges) return state;
      return { nodesById: newNodesById, rebuildCount: rebuildCount + 1 };
    });

    if (hasChanges) get()._rebuildChildrenMap();
  },

  expandAllNodes: (): { count: number } => {
    let count = 0;

    set(state => {
      const { nodesById, rebuildCount } = state;
      const newNodesById = { ...nodesById };
      Object.keys(nodesById).forEach(edgeKey => {
        const node = nodesById[edgeKey];
        if (node.expanded) return;
        newNodesById[edgeKey] = { ...node, expanded: true };
        _setExpandedLocal(node.id, true);
        count++;
      });
      if (count === 0) return state;
      return { nodesById: newNodesById, rebuildCount: rebuildCount + 1 };
    });

    if (count > 0) get()._rebuildChildrenMap();
    log.log(`[DataspaceTreeStore] expandAllNodes: expanded ${count} nodes`);
    return { count };
  },

  collapseAllNodes: (): { count: number } => {
    let count = 0;

    set(state => {
      const { nodesById, rebuildCount } = state;
      const newNodesById = { ...nodesById };
      Object.keys(nodesById).forEach(edgeKey => {
        const node = nodesById[edgeKey];
        if (!node.expanded) return;
        newNodesById[edgeKey] = { ...node, expanded: false };
        _setExpandedLocal(node.id, false);
        count++;
      });
      if (count === 0) return state;
      return { nodesById: newNodesById, rebuildCount: rebuildCount + 1 };
    });

    if (count > 0) get()._rebuildChildrenMap();
    log.log(`[DataspaceTreeStore] collapseAllNodes: collapsed ${count} nodes`);
    return { count };
  },

  addAliasEdge: async (entityId: string, parentId: string | null) => {
    const { initialized } = get();
    if (!initialized) { log.error('[addAliasEdge] Tree not initialized'); return; }

    const edgeKey = getEdgeKey(entityId, parentId);
    if (get().nodesById[edgeKey]) { log.log('[addAliasEdge] Edge already exists:', edgeKey); return; }

    const siblings = get().getChildNodeIds(parentId);
    const lastSiblingPos = siblings.length > 0 ? get().nodesById[getEdgeKey(siblings[siblings.length - 1], parentId)]?.pos : null;
    const pos = generatePositionBetween(lastSiblingPos, null);

    const newNode: TreeNode = { id: entityId, parentId, pos, expanded: false, kind: 'alias' };
    get()._applyAddNode(edgeKey, newNode);

    const { writeEdgeToOrgYDoc } = await import('./orgYDocStore');
    writeEdgeToOrgYDoc({ parentId, childId: entityId, pos, expanded: false, kind: 'alias' });
    log.log('[addAliasEdge] ✅ Alias edge written to Y.Doc:', edgeKey);
  },

  ensureNodeExists: (nodeId: string, parentId: string | null, kind: 'canonical' | 'alias' = 'canonical') => {
    const { nodesById, initialized, dataspaceId } = get();
    if (!initialized || !dataspaceId) {
      log.log('[DataspaceTreeStore] Tree not initialized, skipping node creation');
      return;
    }
    if (nodeId === parentId) {
      log.error('[DataspaceTreeStore] ensureNodeExists: nodeId === parentId would create a self-loop, rejecting:', nodeId?.slice(-8));
      return;
    }
    const edgeKey = getEdgeKey(nodeId, parentId);
    if (nodesById[edgeKey]) return;

    // Validate parentId: if non-null, it must be a known node in this tree OR the
    // dataspaceId itself (Space root). Reject bogus parentIds to prevent ghost edges
    // that would persist to MongoDB via the Y.Doc save path.
    if (parentId !== null) {
      const parentExists = Object.values(nodesById).some(n => n.id === parentId);
      if (!parentExists && parentId !== dataspaceId) {
        log.warn('[DataspaceTreeStore] ensureNodeExists: parentId not found in tree:', parentId?.slice(-8));
        return;
      }
    }

    const siblings = get().getChildNodeIds(parentId);
    const lastSiblingPos = siblings.length > 0 ? nodesById[getEdgeKey(siblings[siblings.length - 1], parentId)]?.pos : null;
    const pos = generatePositionBetween(lastSiblingPos, null);
    const newNode: TreeNode = { id: nodeId, parentId, pos, expanded: false, kind };

    get()._applyAddNode(edgeKey, newNode);
    import('./orgYDocStore').then(({ writeEdgeToOrgYDoc }) => {
      writeEdgeToOrgYDoc({ parentId, childId: nodeId, pos, expanded: false, kind });
    }).catch(() => {});
  },
  
  // ========================================================================
  // Queries (O(1) lookups)
  // ========================================================================
  
  getRootNodeIds: () => {
    const { childrenByParentId } = get();
    return childrenByParentId.get(null) || [];
  },
  
  getChildNodeIds: (parentId: string | null) => {
    const { childrenByParentId } = get();
    return childrenByParentId.get(parentId) || [];
  },
  
  getNode: (nodeId: string, parentId?: string | null) => {
    // ✅ ALIAS SUPPORT: If parentId provided, look up specific edge; otherwise find any edge for this entity
    if (parentId !== undefined) {
      const edgeKey = getEdgeKey(nodeId, parentId);
      return get().nodesById[edgeKey];
    }
    // Find first node for this entity (backward compatibility)
    const nodesById = get().nodesById;
    return Object.values(nodesById).find(n => n.id === nodeId);
  },
  
  // ========================================================================
  // Safe Accessor Methods - Prevent nodesById Key Format Bugs
  // ========================================================================
  
  /**
   * Get a node by edge (childId + parentId)
   * ✅ SAFE: Encapsulates edge key format
   */
  getNodeByEdgeKey: (childId: string, parentId: string | null) => {
    const edgeKey = getEdgeKey(childId, parentId);
    return get().nodesById[edgeKey];
  },
  
  /**
   * Get all nodes (edges) for an entity (supports aliases - same entity in multiple locations)
   * ✅ SAFE: Returns all edges, not just first one
   */
  getNodesByEntityId: (entityId: string) => {
    const { nodesById } = get();
    return Object.keys(nodesById)
      .filter(key => nodesById[key].id === entityId)
      .map(key => nodesById[key]);
  },
  
  /**
   * Check if a specific edge exists
   * ✅ SAFE: Encapsulates edge key format
   */
  hasEdge: (childId: string, parentId: string | null) => {
    const edgeKey = getEdgeKey(childId, parentId);
    return !!get().nodesById[edgeKey];
  },
  
  /**
   * Get expanded state for an entity
   * ✅ SAFE: Handles multiple edges (returns true if ANY edge is expanded)
   */
  getExpandedState: (entityId: string) => {
    const { nodesById } = get();
    const matchingNodes = Object.keys(nodesById)
      .filter(key => nodesById[key].id === entityId)
      .map(key => nodesById[key]);
    return matchingNodes.some((node: TreeNode) => node.expanded);
  },
  
  // ========================================================================
  // Legacy Methods (for backward compatibility)
  // ========================================================================
  
  isExpanded: (nodeId: string, parentId?: string | null) => {
    const node = get().getNode(nodeId, parentId);
    return node?.expanded ?? false;
  },
  
  hasChildren: (nodeId: string) => {
    return get().getChildNodeIds(nodeId).length > 0;
  },
  
  // ========================================================================
  // Internal Mutations
  // ========================================================================
  
  _applyAddNode: (edgeKey: string, node: TreeNode, operation?: TreeOperation) => {
    if (!operation) {
      console.log(`[TreeStore] _applyAddNode (remote): key=${edgeKey.slice(-16)} child=${node.id.slice(-8)} parent=${node.parentId?.slice(-8) ?? 'ROOT'}`)
    }
    const nodeWithLocalExpanded = { ...node, expanded: _isExpandedLocal(node.id) };
    set(state => ({
      nodesById: { ...state.nodesById, [edgeKey]: nodeWithLocalExpanded },
      rebuildCount: state.rebuildCount + 1,
    }));
    get()._rebuildChildrenMap();
  },

  _applyRemoveNode: (edgeKey: string, options: { persist?: boolean } = {}) => {
    const node = get().nodesById[edgeKey];
    if (!node) {
      log.warn('[DataspaceTreeStore] _applyRemoveNode: node not found for key:', edgeKey.slice(-12));
      return;
    }
    log.log('[DataspaceTreeStore] 🗑️ Removing edge:', { edgeKey: edgeKey.slice(-16), nodeId: node.id.slice(-8) });

    set(state => {
      if (!state.nodesById[edgeKey]) return state;
      const newNodesById = { ...state.nodesById };
      delete newNodesById[edgeKey];
      return { nodesById: newNodesById, rebuildCount: state.rebuildCount + 1 };
    });

    get()._rebuildChildrenMap();

    if (options.persist) {
      // Delete from Y.Doc — server-side observer records it for the next writeState call
      import('./orgYDocStore').then(({ deleteEdgeFromOrgYDoc }) => {
        deleteEdgeFromOrgYDoc(node.id, node.parentId);
      }).catch(() => {});
    }
  },
  
  _applyEdgeBatch: (adds: [string, TreeNode][], removes: string[]) => {
    if (adds.length === 0 && removes.length === 0) return;
    set(state => {
      const newNodesById = { ...state.nodesById };
      for (const edgeKey of removes) {
        delete newNodesById[edgeKey];
      }
      for (const [edgeKey, node] of adds) {
        newNodesById[edgeKey] = { ...node, expanded: _isExpandedLocal(node.id) };
      }
      return { nodesById: newNodesById, rebuildCount: state.rebuildCount + 1 };
    });
    get()._rebuildChildrenMap();
  },

  _rebuildChildrenMap: () => {
    const startTime = performance.now();
    const { nodesById } = get();
    const newMap = new Map<string | null, string[]>();
    
    // ⚡ PERFORMANCE FIX: Precompute position map BEFORE sorting
    // Keyed by edgeKey (parentId:entityId) so alias edges with different parents
    // never collide — each (child, parent) pair has its own pos.
    const posMap = new Map<string, string>();
    
    // ✅ ALIAS SUPPORT: Build map from edge nodes (keyed by edgeKey)
    // Each node represents an edge (parent -> child relationship)
    Object.entries(nodesById).forEach(([edgeKey, node]) => {
      posMap.set(edgeKey, node.pos);
      const existing = newMap.get(node.parentId) || [];
      // Only add if not already in list (de-duplicate)
      if (!existing.includes(node.id)) {
        newMap.set(node.parentId, [...existing, node.id]);
      }
    });

    // Cycle detection: walk from each root and warn on back-edges
    const hasCycle = (() => {
      const visited = new Set<string>();
      const inStack = new Set<string>();
      const dfs = (id: string): boolean => {
        if (inStack.has(id)) return true;
        if (visited.has(id)) return false;
        visited.add(id);
        inStack.add(id);
        for (const childId of newMap.get(id) || []) {
          if (dfs(childId)) {
            console.warn('[DataspaceTreeStore] ⚠️ Cycle detected in tree: edge', id, '→', childId);
            return true;
          }
        }
        inStack.delete(id);
        return false;
      };
      for (const id of Array.from(new Set(Array.from(newMap.values()).flat()))) {
        if (!visited.has(id) && dfs(id)) return true;
      }
      return false;
    })();

    if (hasCycle) {
      console.error('[DataspaceTreeStore] ❌ Tree contains cycles! This will cause infinite recursion when rendering expanded nodes.');
    }
    
    // ⚡ OPTIMIZED: Sort using precomputed position map - O(m log m) instead of O(m² × n)
    newMap.forEach((children, parentId) => {
      children.sort((a, b) => {
        // ✅ O(1) map lookup keyed by edgeKey so alias edges don't collide
        const posA = posMap.get(getEdgeKey(a, parentId)) || '';
        const posB = posMap.get(getEdgeKey(b, parentId)) || '';
        return posA.localeCompare(posB);
      });
    });
    
    const elapsed = performance.now() - startTime;
    
    if (VERBOSE_LOGGING) {
      log.log('[_rebuildChildrenMap] 🔄 Rebuilt children map:', {
        totalParents: newMap.size,
        totalEdges: Object.keys(nodesById).length,
        rebuildTime: `${elapsed.toFixed(2)}ms`,
        sampleParents: Array.from(newMap.entries()).slice(0, 3).map(([p, c]) => ({
          parent: p?.slice(-8) || 'null',
          childCount: c.length
        }))
      });
    }
    
    // Log performance warning if rebuild is slow
    if (elapsed > 10) {
      console.warn(`[DataspaceTreeStore] ⚠️ Slow rebuild: ${elapsed.toFixed(2)}ms for ${Object.keys(nodesById).length} nodes`);
    }
    
    set({ childrenByParentId: newMap     });
  },
  
  // ========================================================================
  // Persistence is now handled via org Y.Doc (orgYDocStore.ts):
  //   - Writes: writeEdgeToOrgYDoc() / deleteEdgeFromOrgYDoc()
  //   - Server: org room debounced writeState → MongoDB bulkWrite
  // _scheduleFlush, _flushToBackend, refreshEdges are removed.
  // ========================================================================
  
  // ========================================================================
  // Cleanup
  // ========================================================================
  
  /**
   * ✅ NEW: Migrate entities for a specific space (lazy migration)
   * Called when SpaceSidebarV2 detects no children for a space
   */
  migrateSpaceEntities: async (spaceId: string) => {
    const { dataspaceId } = get();
    if (!dataspaceId) {
      log.error('[DataspaceTreeStore] Cannot migrate - no dataspaceId');
      return;
    }
    
    log.log('[DataspaceTreeStore] 🔄 Migrating entities for space:', spaceId);
    
    // Get all entities in this space
    const entitiesInSpace = useEntityStore.getState().entities.filter(
      (e: BaseEntityType) => (e as any).spaceId === spaceId && isDisplayableEntity(e)
    );
    
    log.log('[DataspaceTreeStore] Found', entitiesInSpace.length, 'entities in space');
    
    if (entitiesInSpace.length === 0) return;
    
    // Check which ones already have edges (check ALL edges, not just space root!)
    // ✅ CRITICAL FIX: Check if entity has ANY edge, not just edges at space root
    // This prevents creating duplicate edges when entities are moved into folders
    const existingChildIds = new Set(
      Object.values(get().nodesById)
        .map((n: TreeNode) => n.id)  // ← Changed: removed parent filter!
    );
    
    const entitiesToMigrate = entitiesInSpace.filter((e: BaseEntityType) => !existingChildIds.has(e._id));
    
    if (entitiesToMigrate.length === 0) {
      log.log('[DataspaceTreeStore] All entities already have edges');
      return;
    }
    
    log.log('[DataspaceTreeStore] Migrating', entitiesToMigrate.length, 'entities');
    
    // Group by parent (from skeleton.treePosition or groupIds)
    const entitiesByParent = new Map<string | null, BaseEntityType[]>();
    
    entitiesToMigrate.forEach((entity: BaseEntityType) => {
      const treePos = (entity.skeleton as any)?.treePosition;
      let parentId: string | null;
      
      if (treePos && treePos.parentId) {
        parentId = treePos.parentId;
      } else {
        const groupIds = (entity.skeleton as any)?.groupIds || [];
        parentId = groupIds.length > 0 ? groupIds[0] : spaceId; // Default to space root
      }
      
      const group = entitiesByParent.get(parentId) || [];
      group.push(entity);
      entitiesByParent.set(parentId, group);
    });
    
    // Create edges with positions
    const edgesToCreate: any[] = [];
    
    entitiesByParent.forEach((entitiesInGroup, parentId) => {
      // Sort by existing treePosition or alphabetically
      entitiesInGroup.sort((a, b) => {
        const posA = (a.skeleton as any)?.treePosition?.pos || a.name;
        const posB = (b.skeleton as any)?.treePosition?.pos || b.name;
        return String(posA).localeCompare(String(posB));
      });
      
      entitiesInGroup.forEach((entity: BaseEntityType, index: number) => {
        const pos = generateCleanPosition(index);
        const treePos = (entity.skeleton as any)?.treePosition;
        
        edgesToCreate.push({
          parentId,
          childId: entity._id,
          pos,
          expanded: treePos?.expanded || false,
          kind: 'canonical'
        });
      });
    });
    
    // Write edges to Y.Doc (server persists to MongoDB via debounced writeState)
    try {
      const { writeEdgeToOrgYDoc } = await import('./orgYDocStore');

      edgesToCreate.forEach((edge: any) => {
        const node: TreeNode = { id: edge.childId, parentId: edge.parentId, pos: edge.pos, expanded: edge.expanded, kind: edge.kind };
        const edgeKey = getEdgeKey(edge.childId, edge.parentId);
        get()._applyAddNode(edgeKey, node);
        writeEdgeToOrgYDoc({ parentId: edge.parentId, childId: edge.childId, pos: edge.pos, expanded: edge.expanded, kind: edge.kind });
      });

      get()._rebuildChildrenMap();
      log.log('[DataspaceTreeStore] ✅ Space migration complete -', edgesToCreate.length, 'edges written to Y.Doc');

    } catch (error) {
      log.error('[DataspaceTreeStore] ❌ Space migration failed:', error);
      console.error('❌ [DataspaceTreeStore] Migration error details:', error);
      // "Some documents may not be in their correct folders. Please refresh the page."
    }
  },
  
  cleanup: () => {
    const { entityStoreUnsubscribe, entityEventUnsubscribe } = get();
    if (entityStoreUnsubscribe) entityStoreUnsubscribe();
    if (entityEventUnsubscribe) entityEventUnsubscribe();
    set({
      dataspaceId: null,
      nodesById: {},
      childrenByParentId: new Map(),
      initialized: false,
      rebuildCount: 0,
      entityStoreUnsubscribe: null,
      entityEventUnsubscribe: null,
    });
  },
  
  // ========================================================================
  // Debug
  // ========================================================================
  
  debugPrintTree: () => {
    const { nodesById, getRootNodeIds, getChildNodeIds } = get();
    const entityStore = useEntityStore.getState();
    
    log.log('\n🌲 DATASPACE TREE:');
    
    const printNode = (nodeId: string, depth: number = 0) => {
      const node = nodesById[nodeId];
      const entity = entityStore.entities.find((e: BaseEntityType) => e._id === nodeId);
      const indent = '  '.repeat(depth);
      const icon = node?.expanded ? '📂' : '📁';
      
      log.log(`${indent}${icon} ${entity?.name || 'Unknown'} (${node?.pos || 'no-pos'}) [${nodeId.slice(-4)}]`);
      
      if (node?.expanded) {
        const children = getChildNodeIds(nodeId);
        children.forEach(childId => printNode(childId, depth + 1));
      }
    };
    
    const roots = getRootNodeIds();
    log.log(`Root level (${roots.length} items):`);
    roots.forEach((id: string) => printNode(id, 0));
    log.log('\n');
  }
}));

export default useDataspaceTreeStore;

// No beforeunload handler needed — Y.Doc writes are synchronously committed to
// the WebSocket provider's pending-updates queue. The server's debounced writeState
// flushes to MongoDB when the connection closes (last-client-disconnect handler).
